import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import type { Language } from '@shared/types';

/**
 * Изборът на език — в лентата, и записан в ПРОФИЛА.
 *
 * 🔴 Записът минава през Cloud Function, а не пряко в базата, защото една и
 * съща настройка живее на ДВЕ места, в различни документи:
 *   `users/{uid}.preferredLanguage`            командва ЕКРАНА
 *   `families/{id}.parentN.preferredLanguage`  командва ПИСМАТА (11 четения)
 * Второто е недостъпно за родителя по правила (`families`: само админ), и това
 * е правилно. Затова сървърът проверява кой вика и пипа САМО неговия ред.
 * ⚠️ Пряк `updateDoc` в `users` би сменил екрана и оставил писмата на стария
 * език — точно прекъснатата верига, която задачата маха.
 *
 * ⚠️ Мястото е лентата, а не настройките, по решение на owner-а и с по-добрия
 * довод: родител, който се озове на непознат език, не бива да търси превключвателя
 * в меню, което не може да прочете. Един клик, отвсякъде.
 */

const ЕЗИЦИ: ReadonlyArray<{ код: Language; кратко: string; име: string }> = [
  { код: 'bg', кратко: 'BG', име: 'Български' },
  { код: 'de', кратко: 'DE', име: 'Deutsch' },
  { код: 'en', кратко: 'EN', име: 'English' },
];

export interface LanguageSwitcherProps {
  /**
   * `bar` — компактен, за тъмната лента горе.
   * `page` — едри бутони, за екрана с настройките.
   * `auth` — над картата на екраните преди вход (вход, забравена парола,
   *   /auth/action), в цвета на училището. На „Забравена парола“ изборът решава
   *   и езика на писмото (`resetPassword` чете езика на екрана).
   * ⛔ `bar` и `page` споделят ЕДНО поведение: разликата е само външна. Разделят
   * ли се, местата пак ще се разминат — точно дефектът, който махаме.
   * ⚠️ `auth` е НАРОЧНО различен и по поведение: сменя само екрана, никога не
   * пише в профила (причината — при `избери`). Не го „уеднаквявай“.
   */
  variant?: 'bar' | 'page' | 'auth';
  /** Цветът на избрания език във вида `auth` (основният цвят на училището). */
  activeColor?: string;
  className?: string;
}

export default function LanguageSwitcher({ variant = 'bar', activeColor, className = '' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [записва, setЗаписва] = useState<Language | null>(null);

  const текущ = (i18n.language || 'bg').split('-')[0];

  const избери = async (код: Language) => {
    if (код === текущ) return;

    // Екранът се сменя ВЕДНАГА — изборът не чака мрежата.
    await i18n.changeLanguage(код);

    // Преди вход няма профил, в който да се пише; входният екран носи свой избор.
    // ⚠️ Видът `auth` НЕ пише в профила и при влязъл потребител: админ, отворил на
    // училищния компютър връзката за нова парола на родител (/auth/action), сменя
    // езика, за да я прочете — не своя профил. (Ръчните копия преди него — също.)
    if (!user || variant === 'auth') return;

    try {
      setЗаписва(код);
      const запиши = httpsCallable<{ language: Language }, unknown>(functions, 'setPreferredLanguage');
      await запиши({ language: код });
    } catch (error) {
      // ⚠️ Известно ограничение, назовано нарочно: провален запис оставя екрана
      // на новия език, а профила — на стария. Симптомът се вижда чак при
      // следващото зареждане (езикът „се връща“). Съобщение в лентата не слагам
      // без дума на owner-а; логът остава следата.
      console.error('Не успях да запиша избрания език в профила:', error);
    } finally {
      setЗаписва(null);
    }
  };

  if (variant === 'auth') {
    // Без aria-label: видимият текст („БГ“, „DE“) Е името на бутона — етикет
    // „Български“ би го разминал с видимото (WCAG 2.5.3). Пълното име — в title.
    return (
      <div className={`flex justify-center gap-2 ${className}`} role="group" aria-label="Sprache / Език / Language">
        {ЕЗИЦИ.map(({ код, кратко, име }) => (
          <button
            key={код}
            type="button"
            onClick={() => избери(код)}
            aria-pressed={текущ === код}
            title={име}
            disabled={записва !== null}
            className={`px-3 py-1 rounded font-medium text-sm transition-colors ${
              текущ === код
                ? `text-white ${activeColor ? '' : 'bg-primary-600'}`
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            style={текущ === код && activeColor ? { backgroundColor: activeColor } : undefined}
          >
            {код === 'bg' ? 'БГ' : кратко}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'page') {
    return (
      <div className={`flex flex-wrap gap-2 sm:gap-4 ${className}`}>
        {ЕЗИЦИ.map(({ код, име }) => (
          <button
            key={код}
            type="button"
            onClick={() => избери(код)}
            aria-pressed={текущ === код}
            disabled={записва !== null}
            className={`btn ${текущ === код ? 'btn-primary' : 'btn-secondary'}`}
          >
            {име}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-0.5 ${className}`} role="group" aria-label="Sprache / Език / Language">
      {ЕЗИЦИ.map(({ код, кратко, име }) => (
        <button
          key={код}
          type="button"
          onClick={() => избери(код)}
          aria-pressed={текущ === код}
          aria-label={име}
          title={име}
          disabled={записва !== null}
          className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
            текущ === код
              ? 'bg-white text-primary-700'
              : 'text-primary-100 hover:bg-white/15'
          }`}
        >
          {кратко}
        </button>
      ))}
    </div>
  );
}
